/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React, { useEffect } from "react";
import styled from "styled-components";
import Container from "../../components/Container";
import NavigationHeader from "../../components/NavigationHeader";
import LoadingIndicator from "../../components/LoadingIndicator";
import ClientBookingsItem from "../../components/client/ClientBookingsItem";
import { useClientBookings } from "../../lib/react-query/useClientBookings";
import Button from "../../components/ui/Button";
import Stack from "../../components/ui/Stack";
import useWidgetCallbackParams from "../../hooks/useWidgetCallbackParams";
import { ROUTES } from "../../constants/constants";
import { useNavigate } from "react-router-dom";
import Text from "../../components/ui/Text";
import Icon from "../../components/ui/Icon";
import { useBookingApiEntities } from "../../hooks/useBookingApiEntities";
import { TabContent, Tabs, TabsItem, TabsList } from "../../components/ui/Tabs";

const StyledClientProfile = styled.div`
  display: grid;
  gap: 24px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 420px;
  margin: 0 auto;
  gap: 24px;
  padding-block: 48px;
  text-align: center;
`;

const BookingsEmptyState = () => {
  const navigate = useNavigate();

  return (
    <EmptyState>
      <Icon.CalendarEmpty size={Icon.SIZE.XL} color="primary" />
      <Text size="large" color="dark">
        {"empty_client_bookings_page"}
      </Text>
      <Button variant="primary" title="Book now" onClick={() => navigate(ROUTES.SERVICES)} />
    </EmptyState>
  );
};

const ClientBookings = () => {
  const { clearParams } = useWidgetCallbackParams();
  const { includeEntities: bookingIncludeEntities } = useBookingApiEntities("booking");
  const {
    isLoading: isUpcomingLoading,
    isFetchingNextPage: isFetchingNextUpcoming,
    data: upcomingBookings,
    hasNextPage: hasMoreUpcoming,
    fetchNextPage: fetchNextUpcoming,
  } = useClientBookings({
    "filter[upcoming_only]": 1,
    "filter[confirmed_only]": 0,
    "include": bookingIncludeEntities,
  });

  const {
    isLoading: isCompletedLoading,
    isFetchingNextPage: isFetchingNextCompleted,
    data: completedBookings,
    hasNextPage: hasMoreCompleted,
    fetchNextPage: fetchNextCompleted,
  } = useClientBookings({
    "filter[confirmed_only]": 0,
    "filter[past_only]": 1,
    "include": bookingIncludeEntities,
    "order_field": "date_start",
    "order_direction": "desc",
  });

  useEffect(() => {
    clearParams();
  }, [clearParams]);

  if (isUpcomingLoading || isCompletedLoading) {
    return (
      <Container>
        <NavigationHeader title="My bookings" back={ROUTES.HOME} />
        <LoadingIndicator page />
      </Container>
    );
  }

  return (
    <Container>
      <NavigationHeader title="My bookings" back={ROUTES.HOME} />
      <StyledClientProfile>
        {!upcomingBookings.pages[0].data.length && !completedBookings.pages[0].data.length && (
          <BookingsEmptyState />
        )}

        {(!!upcomingBookings.pages[0].data.length || !!completedBookings.pages[0].data.length) && (
          <Tabs defaultTab="upcoming">
            <TabsList>
              <TabsItem title="Upcoming bookings" id="upcoming" />
              <TabsItem title="Previous bookings" id="previous" />
            </TabsList>
            <TabContent id="upcoming">
              {!!upcomingBookings.pages[0].data.length ? (
                <Stack $gap="1rem">
                  {upcomingBookings.pages.map((page) =>
                    page.data.map((booking) => (
                      <ClientBookingsItem
                        key={booking.id}
                        booking={booking}
                        isUpcoming={true}
                      />
                    ))
                  )}
                  {hasMoreUpcoming && (
                    <Button
                      variant="outline"
                      onClick={() => fetchNextUpcoming()}
                      loading={isFetchingNextUpcoming}
                      title="Load more"
                    />
                  )}
                </Stack>
              ) : (
                <BookingsEmptyState />
              )}
            </TabContent>
            <TabContent id="previous">
              {!!completedBookings.pages[0].data.length ? (
                <Stack $gap="1rem">
                  {completedBookings.pages.map((page) =>
                    page.data.map((booking) => (
                      <ClientBookingsItem
                        key={booking.id}
                        booking={booking}
                      />
                    ))
                  )}
                  {hasMoreCompleted && (
                    <Button
                      variant="outline"
                      onClick={() => fetchNextCompleted()}
                      loading={isFetchingNextCompleted}
                      title="Load more"
                    />
                  )}
                </Stack>
              ) : (
                <BookingsEmptyState />
              )}
            </TabContent>
          </Tabs>
        )}
      </StyledClientProfile>
    </Container>
  );
};

export default ClientBookings;
